import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import {
  createRunTotalAuditData,
  getRunTotalAudit,
  listRunTotalAudits,
  putRunTotalAudit,
  getRunTotalAuditCharts
} from '../Api/RunTotalAudit/RunTotalAuditApi';
import { addError } from './Error';
import { ApiAdvancedFilter, PaginatedResponse } from './GeneralStore';
import { User } from './User';
import { DrawerData } from './Drawer';

export enum AuditStatus {
  NEW = 'new',
  IN_PROGRESS = 'in progress',
  COMPLETED = 'completed',
  ERROR = 'error'
}

export enum AuditType {
  MANUAL = 'manual',
  RPM_SYNC = 'rpm sync',
  DAILY_USE = 'daily use',
  DEPLOYMENT = 'deployment'
}

export interface RunTotalAudit {
  id: string;
  status: AuditStatus;
  auditType: AuditType;
  auditStage?: string;
  beforeSnapshotTimestamp?: string;
  afterSnapshotTimestamp?: string;
  createdBy: User;
  completedAt?: string;
  errorMessage?: string;
  assetTotalRowsBefore?: number;
  assetTotalRowsAfter?: number;
  assetDuplicateCountBefore?: number;
  assetDuplicateCountAfter?: number;
  assetRowsUpdated?: number;
  provisionalAssetTotalRowsBefore?: number;
  provisionalAssetTotalRowsAfter?: number;
  provisionalAssetDuplicateCountBefore?: number;
  provisionalAssetDuplicateCountAfter?: number;
  provisionalAssetRowsUpdated?: number;
  rpmAccountTotalRowsBefore?: number;
  rpmAccountTotalRowsAfter?: number;
  rpmAccountDuplicateCountBefore?: number;
  rpmAccountDuplicateCountAfter?: number;
  rpmAccountRowsUpdated?: number;
  ticketTotalRowsBefore?: number;
  ticketTotalRowsAfter?: number;
  ticketDuplicateCountBefore?: number;
  ticketDuplicateCountAfter?: number;
  ticketRowsUpdated?: number;
  customerBySupplierTotalRowsBefore?: number;
  customerBySupplierTotalRowsAfter?: number;
  customerBySupplierDuplicateCountBefore?: number;
  customerBySupplierDuplicateCountAfter?: number;
  customerBySupplierRowsUpdated?: number;
  customerTotalRowsBefore?: number;
  customerTotalRowsAfter?: number;
  customerDuplicateCountBefore?: number;
  customerDuplicateCountAfter?: number;
  customerRowsUpdated?: number;
  allTablesTotalRowsBefore?: number;
  allTablesTotalRowsAfter?: number;
  allTablesDuplicateCountBefore?: number;
  allTablesDuplicateCountAfter?: number;
  allTablesRowsUpdated?: number;
  createdAt: string;
  updatedAt: string;
}

export interface RunTotalAuditChartPoint {
  date: string;
  [key: string]: string | number | undefined;
}

export interface RunTotalAuditChartsResponse {
  afterTotalSeries: RunTotalAuditChartPoint[];
  changeSeries: RunTotalAuditChartPoint[];
}

const runTotalAuditsSubject = new BehaviorSubject<PaginatedResponse<RunTotalAudit>>(
  {} as PaginatedResponse<RunTotalAudit>
);
const runTotalAuditDetailSubject = new BehaviorSubject<DrawerData | undefined>(undefined);
const auditChartsSubject = new BehaviorSubject<RunTotalAuditChartsResponse | undefined>(undefined);
const runTotalAuditLoadingSubject = new BehaviorSubject<boolean>(false);
const runTotalAuditsLoadingSubject = new BehaviorSubject<boolean>(false);

const clearError = () => addError('run-total-audit', undefined);

const inboundFetchNumberSubject = new BehaviorSubject<number>(0);
export const getRunTotalAuditsSubject = (): BehaviorSubject<PaginatedResponse<RunTotalAudit>> => runTotalAuditsSubject;
export const getRunTotalAuditDetailSubject = (): BehaviorSubject<DrawerData | undefined> => runTotalAuditDetailSubject;
export const getAuditChartsSubject = () => auditChartsSubject;
export const updateRunTotalAuditDetail = (runTotalAuditData: RunTotalAudit) =>
  runTotalAuditDetailSubject.next(runTotalAuditData);
export const getRunTotalAuditLoadingSubject = (): BehaviorSubject<boolean> => runTotalAuditLoadingSubject;
export const getRunTotalAuditsLoadingSubject = (): BehaviorSubject<boolean> => runTotalAuditsLoadingSubject;

export const getRunTotalAuditById = async (id: string) => {
  const audit = runTotalAuditsSubject?.getValue()?.data?.find((a) => a?.id === id);
  if (audit) {
    runTotalAuditDetailSubject.next(audit);
  } else {
    await fetchRunTotalAudit(id);
  }
  return audit || ({} as RunTotalAudit);
};

export const fetchRunTotalAudit = async (id: string) => {
  runTotalAuditLoadingSubject.next(true);
  const { data, error } = await getRunTotalAudit(id);
  if (data) {
    clearError();
    runTotalAuditDetailSubject.next(data);
  }
  if (error) {
    addError('run-total-audit', error.message);
    const statusCode = error.errorResponse?.status || 500;
    return statusCode;
  }
  runTotalAuditLoadingSubject.next(false);
};

export const fetchRunTotalAudits = async (filters?: ApiAdvancedFilter, forceRefresh = false) => {
  const newFetchNumber = inboundFetchNumberSubject.getValue() + 1;
  inboundFetchNumberSubject.next(newFetchNumber);
  if (runTotalAuditsSubject.getValue()?.data?.length > 0 && !forceRefresh) return;

  runTotalAuditsLoadingSubject.next(true);
  const { data, error } = await listRunTotalAudits(filters);
  const currentFetchNumber = inboundFetchNumberSubject.getValue();
  if (data && newFetchNumber === currentFetchNumber) {
    clearError();
    runTotalAuditsSubject.next(data);
  }
  if (error) addError('run-total-audit', error.message);
  newFetchNumber === currentFetchNumber && runTotalAuditsLoadingSubject.next(false);
};

export const createRunTotalAudit = async (startBeforeSnapshot: boolean) => {
  const { data, error } = await createRunTotalAuditData(startBeforeSnapshot);
  if (data) clearError();
  if (error) addError('run-total-audit', error.message);
  return { data, error };
};

export const updateRunTotalAudit = async (id: string, stage: string) => {
  const { data, error } = await putRunTotalAudit(id, stage);
  if (data) {
    clearError();
    runTotalAuditDetailSubject.next(data);
    const currentAudits = runTotalAuditsSubject.getValue();
    const auditData = currentAudits.data;
    if (auditData) {
      const auditIndex = auditData.findIndex((audit) => audit.id === data.id);
      if (auditIndex !== -1) {
        const updatedAuditData = [...auditData];
        updatedAuditData[auditIndex] = data;
        runTotalAuditsSubject.next({
          ...currentAudits,
          data: updatedAuditData
        });
      }
    }
  }
  if (error) addError('run-total-audit', error.message);
  return { data, error };
};

export const fetchAuditCharts = async () => {
  runTotalAuditsLoadingSubject.next(true);
  const { data, error } = await getRunTotalAuditCharts();
  if (data) {
    clearError();
    auditChartsSubject.next(data);
  }
  if (error) addError('run-total-audit', error.message);
  runTotalAuditsLoadingSubject.next(false);
};
